import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { fromEvent } from 'rxjs';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';
import { profileFeature } from '../../state/profile/profile.feature';

import { AuthActions } from '../../state/auth/auth.actions';

type NavItem = {
  key: string;
  label: string;
  path: string;
  icon: 'home' | 'chart' | 'bolt' | 'receipt' | 'wallet' | 'shield' | 'beaker' | 'sparkles' | 'database';
};

@Component({
  selector: 'app-start',
  standalone: true,
  imports: [RouterModule],
  templateUrl: './start.html',
  styleUrl: './start.scss',
})
export class Start {
  private readonly store = inject(Store);
  private readonly auth = inject(Auth);
  private readonly authEmail = signal<string>('');

  readonly profileSig = this.store.selectSignal(profileFeature.selectProfile);

  readonly displayName = computed(() => {
    const p = this.profileSig() as unknown as Record<string, unknown> | null;

    const first = (p?.['firstName'] as string | undefined)?.trim();
    const last = (p?.['lastName'] as string | undefined)?.trim();
    const composed = [first, last].filter(Boolean).join(' ').trim();
    if (composed) return composed;

    const dn = (p?.['displayName'] as string | undefined)?.trim();
    if (dn) return dn;

    const email = (p?.['email'] as string | undefined)?.trim();
    if (email) return email;

    const authEmail = this.authEmail().trim();
    if (authEmail) return authEmail;

    return 'User';
  });

  readonly userInitial = computed(() => {
    const name = this.displayName().trim();
    return (name ? name[0] : 'U').toUpperCase();
  });

  private readonly destroyRef = inject(DestroyRef);

  mobileMenuOpen = signal(false);
  userMenuOpen = signal(false);
  isDark = signal(true);

  constructor() {
    onAuthStateChanged(this.auth, (user) => {
      this.authEmail.set(user?.email ?? '');
    });
  
    // Theme initialisieren
    this.isDark.set(document.documentElement.classList.contains('dark'));
  
    // User-Menü bei Klick außerhalb schließen
    const sub = fromEvent<MouseEvent>(document, 'click').subscribe(() => this.userMenuOpen.set(false));
    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  navItems = signal<NavItem[]>([
    { key: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: 'home' },
    { key: 'predictions', label: 'Predictions', path: '/predictions', icon: 'chart' },
    { key: 'trading', label: 'Trading', path: '/trading', icon: 'bolt' },
    { key: 'orders', label: 'Orders', path: '/orders', icon: 'receipt' },
    { key: 'portfolio', label: 'Portfolio', path: '/portfolio', icon: 'wallet' },
    { key: 'risk', label: 'Risk', path: '/risk', icon: 'shield' },
    { key: 'backtest', label: 'Backtest', path: '/backtest', icon: 'beaker' },
    { key: 'visuals', label: 'Visuals', path: '/visuals', icon: 'sparkles' },
    { key: 'marketdata', label: 'Marketdata', path: '/marketdata', icon: 'database' },
  ]);

  openMobileMenu(): void {
    this.mobileMenuOpen.set(true);
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen.set(false);
  }

  toggleUserMenu(ev: MouseEvent): void {
    ev.stopPropagation();
    this.userMenuOpen.set(!this.userMenuOpen());
  }

  closeUserMenu(ev?: MouseEvent): void {
    if (ev) ev.stopPropagation();
    this.userMenuOpen.set(false);
  }

  toggleTheme(ev: MouseEvent): void {
    ev.stopPropagation();

    const next = !this.isDark();
    this.isDark.set(next);

    // Tailwind dark-mode via class am <html>
    document.documentElement.classList.toggle('dark', next);
  }

  signOut(ev?: MouseEvent): void {
    if (ev) ev.stopPropagation();
    this.userMenuOpen.set(false);
    this.store.dispatch(AuthActions.logoutRequested());
  }

  // --- Icons (inline, ohne extra Lib) ---
  iconPath(icon: NavItem['icon']): string[] {
    switch (icon) {
      case 'home':
        return ['M3 10.5 12 3l9 7.5V21a1.5 1.5 0 0 1-1.5 1.5H4.5A1.5 1.5 0 0 1 3 21V10.5Z', 'M9 22.5V15h6v7.5'];
      case 'chart':
        return ['M4 19V5', 'M4 19h16', 'M8 15l3-3 3 2 4-6'];
      case 'bolt':
        return ['M13 2 3 14h7l-1 8 10-12h-7l1-8Z'];
      case 'receipt':
        return ['M6 2h12v20l-2-1-2 1-2-1-2 1-2-1-2 1V2Z', 'M8 7h8', 'M8 11h8', 'M8 15h6'];
      case 'wallet':
        return ['M3 7a2 2 0 0 1 2-2h14v4H5a2 2 0 0 0 0 4h14v4H5a2 2 0 0 1-2-2V7Z', 'M19 13h2v-2h-2v2Z'];
      case 'shield':
        return ['M12 2 20 6v7c0 5-3.5 9-8 11C7.5 22 4 18 4 13V6l8-4Z'];
      case 'beaker':
        return ['M9 2v6l-5 9a3 3 0 0 0 2.6 4.5h10.8A3 3 0 0 0 20 17l-5-9V2', 'M9 8h6'];
      case 'sparkles':
        return ['M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5L12 2Z', 'M19 13l.9 2.7L23 17l-3.1 1.3L19 21l-.9-2.7L15 17l3.1-1.3L19 13Z'];
      case 'database':
        return ['M4 6c0-2.2 3.6-4 8-4s8 1.8 8 4-3.6 4-8 4-8-1.8-8-4Z', 'M4 6v6c0 2.2 3.6 4 8 4s8-1.8 8-4V6', 'M4 12v6c0 2.2 3.6 4 8 4s8-1.8 8-4v-6'];
    }
  }
}
